/**
 * Utility function to get tracking metadata for video room events
 * This includes PostHog session ID, user ID, and other request metadata
 */

export interface TrackingMetadata {
  user_agent: string;
  user_id: number;
  posthog_session_id: string;
}

/**
 * Get PostHog session ID with proper error handling and fallbacks
 * @returns PostHog session ID or fallback session ID
 */
const getPostHogSessionId = (): string => {
  try {
    // Check if PostHog is available and initialized
    if (typeof window !== 'undefined' && window.posthog) {
      // Try to get session ID using the official PostHog method
      if (typeof window.posthog.get_session_id === 'function') {
        const sessionId = window.posthog.get_session_id();
        if (sessionId) {
          return sessionId;
        }
      }
      
      // Fallback: try to get session ID from PostHog's internal state
      if (window.posthog.config && window.posthog.config.session_id) {
        return window.posthog.config.session_id;
      }
      
      // Another fallback: check if PostHog has a session property
      if (window.posthog.session_id) {
        return window.posthog.session_id;
      }
    }
  } catch (error) {
    console.warn('Could not get PostHog session ID:', error);
  }
  
  // Generate a fallback session ID if PostHog is not available
  const timestamp = Math.floor(Date.now() / 1000);
  return `fallback_session_${timestamp}`;
};

/**
 * Get tracking metadata for video room events
 * Note: IP address is handled by the backend from request headers
 * @param user - The user object from platform context
 * @returns TrackingMetadata object with all available tracking information
 */
export const getTrackingMetadata = (user: { id: number } | null): TrackingMetadata => {
  // Get user agent
  const user_agent = navigator.userAgent || '';

  // Get user ID from the user object
  const user_id = user?.id || 0;

  // Get PostHog session ID with proper fallbacks
  const posthog_session_id = getPostHogSessionId();

  return {
    user_agent,
    user_id,
    posthog_session_id,
  };
};

// Type declaration for window objects
declare global {
  interface Window {
    posthog?: {
      get_session_id?: () => string;
      config?: {
        session_id?: string;
      };
      session_id?: string;
    };
    firebase?: {
      auth: () => {
        currentUser: any;
      };
    };
  }
}
